import React, { useEffect, useState } from "react";
import "./MortgageDetailsTable.style.css";
import { humanReadableDate } from "../../app/utils";

const MRB_APPLICATIONS_FETCH_URL = "/api/all_broker_applications";
const MRB_ARCHIVE_APPLICATION_FETCH_URL = "/api/all_broker_archive_applications";

const groupKey = (row) =>
  [row?.contactName, row?.agencyName, row?.parentAgencyName, row?.companyTrademark]
    .map((v) => (v ?? "").toString().toLowerCase())
    .join("||");

const groupApplications = (apps) => {
  const groups = [];
  const indexByKey = new Map();
  apps.forEach((row) => {
    const key = groupKey(row);
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ key, rows: [] });
    }
    groups[indexByKey.get(key)].rows.push(row);
  });
  return groups;
};

const ContactCell = (props) => {
  const { name, agency, parent_name, trademark } = props;

  return (
    <>
      <div style={{ fontWeight: "bold" }}>{name}</div>
      {parent_name && (
        <div>
          <span>{parent_name}</span>
          <span>, operating as </span>
          <span>{agency}</span>
        </div>
      )}
      {!parent_name && trademark && (
        <div>
          <span>{agency}</span>
          <span> operating as </span>
          <span>{trademark}</span>
        </div>
      )}
      {!parent_name && !trademark && <div>{agency}</div>}
    </>
  );
};

const columns = [
  {
    id: "contactName",
    label: "Individual information, under current brokerage",
    align: "left",
    minWidth: 320
  },
  {
    id: "licenceNumber",
    label: "Licence number",
    align: "center",
    minWidth: 140
  },
  {
    id: "licenceSubType",
    label: "Licence type",
    minWidth: 140,
    align: "center"
  },
  {
    id: "originalIssuedDate",
    label: "Original Issued date",
    minWidth: 164,
    align: "center"
  },
  {
    id: "endDate",
    label: "Expiry date",
    minWidth: 164,
    align: "center"
  },
  {
    id: "licenceStatus",
    label: "Licence status",
    minWidth: 140,
    align: "center"
  },
  {
    id: "licenceStatusChangeDate",
    label: "Licence status change date",
    minWidth: 164,
    align: "center"
  },
  {
    id: "administrativePenaltiesNote",
    label: "Comments",
    minWidth: 300,
    align: "center"
  }
];

export default function BrokerDetailsTable() {
  const [applications, setApplications] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState("loading");

  useEffect(() => {
    const fetchPromises = [];
    fetchPromises.push(fetch(MRB_APPLICATIONS_FETCH_URL).then((resp) => resp.json()));
    fetchPromises.push(fetch(MRB_ARCHIVE_APPLICATION_FETCH_URL).then((resp) => resp.json()));

    Promise.all(fetchPromises)
      .then((responses) => {
        if (responses && Array.isArray(responses) && responses.length === 2) {
          const apps = responses[0];
          const archiveApps = responses[1];
          let applications = [];

          if (apps && apps["applicationsList"]) {
            applications = [...apps["applicationsList"]];
          }

          if (archiveApps && archiveApps["applicationsList"]) {
            applications = [...applications, ...archiveApps["applicationsList"]];
          }

          setLoadingStatus(null);
          const cmpFunc = (app1, app2) => {
            const nameCmp = app1.contactName
              ?.toLowerCase()
              .localeCompare(app2.contactName?.toLowerCase());
            if (nameCmp !== 0) return nameCmp;
            return groupKey(app1).localeCompare(groupKey(app2));
          };
          setApplications(applications.sort(cmpFunc));
        } else {
          setLoadingStatus("error");
        }
      })
      .catch((err) => setLoadingStatus("error"));
  }, []);

  if (loadingStatus === "loading") {
    return <div className="status loading">Loading...</div>;
  } else if (loadingStatus === "error") {
    return (
      <div className="status error">
        <div>Error loading data.</div>
        <div>Please try again later.</div>
      </div>
    );
  }

  return (
    <>
      <div className="expiryMsgNote">
        All mortgage licenses expire on October 31<sup>st</sup> each year.
      </div>
      <div className="mraDetailsTable">
        <table>
          <tbody>
            <tr>
              {columns.map((column) => {
                return (
                  <th key={column.id} className="tableHeader" style={{ minWidth: column.minWidth, width: "100%" }}>
                    {column.label}
                  </th>
                );
              })}
            </tr>
            {groupApplications(applications).flatMap((group, groupIdx) =>
              group.rows.map((row, rowIdx) => {
                return (
                  <tr
                    className="tableRow"
                    key={row["licenceNumber"]}
                    style={{
                      backgroundColor: groupIdx % 2 === 0 ? "#ffffff" : "#eeeeee",
                      borderTop: 0 === rowIdx ? "2px solid #838383" : "1px solid #c2c2c2"
                    }}
                  >
                    {columns.map((column) => {
                      if (column.id === "contactName") {
                        if (rowIdx !== 0) return null;
                        return (
                          <td
                            className="tableCell"
                            key={`${column.id}-${row["licenceNumber"]}`}
                            rowSpan={group.rows.length}
                          >
                            <ContactCell
                              name={row[column.id]}
                              agency={row["agencyName"]}
                              parent_name={row["parentAgencyName"]}
                              trademark={row["companyTrademark"]}
                            />
                          </td>
                        );
                      }
                      if (column.id === "licenceStatusChangeDate") {
                        //Do not show status change date if licence is Active
                        if (row["licenceStatus"] === "Active" || row["licenceStatus"] === "Expired") {
                          return <td key={`${column.id}-${row["licenceNumber"]}`} />;
                        }
                        const date = row[column.id];
                        return (
                          <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                            {date ? humanReadableDate(date) : ""}
                          </td>
                        );
                      }
                      else if(column.id === "originalIssuedDate") {
                        const date = row[column.id];
                        return (
                          <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                            {date ? humanReadableDate(date) : ""}
                          </td>
                        );
                      }
                      else if(column.id === "endDate") {
                        if (!(row["licenceStatus"] === "Active" || row["licenceStatus"] === "Expired")) {
                          return <td />;
                        }
                        const date = row[column.id];
                        return (
                          <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                            {date ? humanReadableDate(date) : ""}
                          </td>
                        );
                      }
                      return (
                        <td className="tableCell" key={`${column.id}-${row["licenceNumber"]}`}>
                          {row[column.id]}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
